import { Link } from 'react-router-dom';
import { LegalLayout } from './LegalLayout.js';
import { Clause, List, Mono, P, Plain, Steps, Terms } from './prose.js';

const EFFECTIVE = '2026-08-05';

/**
 * The page Meta requires an app to publish as "Data Deletion Instructions".
 *
 * A reviewer checks that it exists, that it is reachable without logging in,
 * and that a person could actually follow it. So it is written as two routes a
 * person can complete — the one through Meta's own settings, which reaches our
 * callback automatically, and the one for people who never had a Facebook
 * account to begin with.
 */
export function DataDeletionPage() {
  return (
    <LegalLayout
      label="Data deletion"
      title="How to delete your data"
      effective={EFFECTIVE}
      standfirst={
        <>
          Two routes, both of which end with the same thing: your conversations, your messages and
          the profile record created from them are erased, and you are given a confirmation code you
          can check on a page of your own.
        </>
      }
    >
      <Clause n={1} title="Route one — through Meta, if you wrote to a business on Facebook or Instagram">
        <P>
          This is the fastest route. Removing the Legate app from your Meta account sends us a signed
          deletion request automatically; nobody at either company has to read an email first.
        </P>

        <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
          On Facebook
        </div>
        <div className="mt-3">
          <Steps
            items={[
              <>Open Facebook and go to <Mono>Settings &amp; privacy</Mono> → <Mono>Settings</Mono>.</>,
              <>In the left column choose <Mono>Apps and websites</Mono>.</>,
              <>Find <strong className="text-ink font-medium">Legate</strong> in the list of apps
              logged in with Facebook and open <Mono>View and edit</Mono>.</>,
              <>Press <Mono>Remove</Mono>, and tick the option to delete the activity the app holds
              when Facebook offers it.</>,
              <>Confirm. Facebook calls our deletion callback within seconds and shows you a link to
              the status page described in clause 3.</>,
            ]}
          />
        </div>

        <div className="mt-8 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
          On Instagram
        </div>
        <div className="mt-3">
          <Steps
            items={[
              <>Open your profile and go to <Mono>Settings and privacy</Mono>.</>,
              <>Choose <Mono>Website permissions</Mono> → <Mono>Apps and websites</Mono>.</>,
              <>Under <Mono>Active</Mono>, find <strong className="text-ink font-medium">Legate</strong>{' '}
              and press <Mono>Remove</Mono>.</>,
              <>Confirm, and choose the option to delete your activity where it is offered.</>,
            ]}
          />
        </div>

        <P>
          Meta renames these screens from time to time. Whatever the labels say, the setting lives
          under apps and websites in both products; if you cannot find it, use route two and we will
          do it for you.
        </P>
      </Clause>

      <Clause n={2} title="Route two — write to us">
        <P>
          Use this if you reached a business through WhatsApp or email, if you never had the app on
          your account, or if the screens above did not lead anywhere.
        </P>
        <Steps
          items={[
            <>Send an email to <Mono>privacy@legate.dev</Mono> with the subject{' '}
            <Mono>Data deletion</Mono>.</>,
            <>Tell us where the conversation happened — the business you were talking to, and the
            Instagram handle, Facebook profile name, WhatsApp number or email address you used. We
            need it only to find the records; we do not ask for identity documents.</>,
            <>We confirm receipt within 3 working days with a confirmation code.</>,
            <>The deletion is carried out and the code turns to <em>completed</em> on its status
            page. Within 30 days at the outside, and normally the same day.</>,
          ]}
        />
        <P>
          If the records belong to a conversation with a particular business, that business is the
          controller of them. We carry out the deletion on their standing instruction and tell them
          it happened; if they object on a legal ground, we tell you which and why.
        </P>
      </Clause>

      <Clause n={3} title="Your confirmation code">
        <P>
          Either route produces a code of the form <Mono>del_</Mono> followed by sixteen characters,
          and a page that shows what happened to your data:{' '}
          <Mono>legate.dev/deletion-status/&lt;code&gt;</Mono>. The page needs no account and no
          login. It lists what was removed, what was kept and why, and the moment the work finished.
        </P>
        <P>
          The record behind that page does not contain your identifier. It stores a keyed digest of
          it, which is enough to recognise a second request from you and useless as a copy of the
          thing you asked us to erase.
        </P>
      </Clause>

      <Clause n={4} title="What is deleted">
        <Terms
          items={[
            {
              term: 'Conversations',
              detail:
                'Every thread you had with a business through Legate, in Messenger, Instagram Direct or WhatsApp.',
            },
            {
              term: 'Messages',
              detail:
                'The full content of those threads — yours and the ones sent to you — including the drafts the agent produced and any that were blocked before sending.',
            },
            {
              term: 'Your identifier in the prospect graph',
              detail:
                'The channel handle that links a record to you is deleted, and the profile record itself goes with it if it existed only because you wrote in.',
            },
            {
              term: 'WhatsApp opt-in',
              detail:
                'The consent record, if you had given one. Without it no template message can be sent to you at all.',
            },
          ]}
        />
      </Clause>

      <Clause n={5} title="What is kept, and why">
        <Plain>
          One record survives on purpose: if you had asked not to be contacted, that objection stays.
        </Plain>
        <P>
          Deleting it is the one outcome an erasure must never produce — it would quietly make you
          contactable again by the very business you had told to stop. The record holds your platform
          id and the fact that you objected, and it is checked before every send. If you want it gone
          as well, say so explicitly at <Mono>privacy@legate.dev</Mono> and we will remove it, having
          told you the consequence first.
        </P>
        <P>Two other things remain, neither of which is about you:</P>
        <List
          items={[
            'Aggregate counters — how many conversations a campaign opened, how many were answered. They carry no message text, no name and no identifier, and their link to your conversation is removed with it.',
            'One audit entry per business, naming the confirmation code and the number of records removed. It is append-only and is the evidence that the deletion was carried out.',
          ]}
        />
      </Clause>

      <Clause n={6} title="If you are the business, not the person">
        <P>
          A customer closing its Legate account has its prospect graph, conversations, playbooks and
          campaigns deleted within 30 days of the account closing. Invoices and the audit trail are
          kept for the statutory period. Write to <Mono>privacy@legate.dev</Mono> from the account
          owner&rsquo;s address to start it; removing a single connected account is immediate and is
          done from the channels screen.
        </P>
      </Clause>

      <Clause n={7} title="Related">
        <P>
          What is collected in the first place, on what basis, and for how long, is set out in the{' '}
          <Link
            to="/privacy"
            className="text-accent hover:text-accent-hover underline underline-offset-4 transition-colors duration-200"
          >
            privacy policy
          </Link>
          . The rules the service will not break are clause 5 of the{' '}
          <Link
            to="/terms"
            className="text-accent hover:text-accent-hover underline underline-offset-4 transition-colors duration-200"
          >
            terms
          </Link>
          .
        </P>
      </Clause>
    </LegalLayout>
  );
}

export default DataDeletionPage;
